/**
 * FlzHub — Main Page Component
 * Twitter-like: virtual scroll, cache, optimistic UI, auto-refresh, search
 */
import { MessageCircle, Users } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import MainLayout from '../components/layout/MainLayout'
import {
    FlzArena,
    FlzChat,
    FlzEmotions,
    FlzExpertRooms,
    FlzFamilyTree,
    FlzHabitat, FlzNeural,
    FlzStories,
    FlzSuggestions,
    FlzTests,
    FlzTopics,
    FlzTraining
} from '../components/os/FlzHub'
import { API_URL, apiFetch } from '../config/api'
import { useAuth } from '../contexts/AuthContext'
import { Agent, Competition, Message, Stats, Suggestion, TabType } from '../types/flzhub'

const PAGE_CACHE_KEY = 'flzhub_page_messages_cache'
const CACHE_TTL = 5 * 60 * 1000

function loadPageCache(): Message[] {
  try {
    const raw = localStorage.getItem(PAGE_CACHE_KEY)
    if (!raw) return []
    const { data, ts } = JSON.parse(raw)
    if (Date.now() - ts > CACHE_TTL) return []
    return data ?? []
  } catch { return [] }
}

function savePageCache(msgs: Message[]) {
  try {
    localStorage.setItem(PAGE_CACHE_KEY, JSON.stringify({ data: msgs.slice(0, 50), ts: Date.now() }))
  } catch { }
}

export default function FlzHubChat() {
  const { user } = useAuth()
  const [messages, setMessages] = useState<Message[]>(() => loadPageCache())
  const [agents, setAgents] = useState<Agent[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [competitions, setCompetitions] = useState<Competition[]>([])
  const [loading, setLoading] = useState(messages.length === 0)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('chat')
  const [search, setSearch] = useState('')
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [newMsgCount, setNewMsgCount] = useState(0)
  const latestIdRef = useRef<number>(0)

  const isAdmin = user?.role === 'admin'
  const isTrainer = user?.role === 'admin' || user?.role === 'trainer'

  const loadData = useCallback(async (silent = false) => {
    try {
      const [mR, aR, sR, sugR, cR] = await Promise.allSettled([
        apiFetch(`${API_URL}/api/flzhub/messages?limit=50`),
        apiFetch(`${API_URL}/api/flzhub/agents`),
        apiFetch(`${API_URL}/api/flzhub/stats`),
        apiFetch(`${API_URL}/api/flzhub/suggestions`),
        apiFetch(`${API_URL}/api/flzhub/competitions`)
      ])

      if (mR.status === 'fulfilled') {
        const d = await mR.value.json()
        const msgs: Message[] = d.messages ?? []
        if (msgs.length > 0) {
          const newestId = msgs[0]?.id ?? 0
          if (silent && latestIdRef.current > 0 && newestId > latestIdRef.current) {
            const newCount = msgs.filter(m => m.id > latestIdRef.current).length
            if (newCount > 0) setNewMsgCount(prev => prev + newCount)
          } else {
            setMessages(msgs)
            savePageCache(msgs)
            setNewMsgCount(0)
          }
          latestIdRef.current = newestId
        }
        setHasMore(msgs.length >= 50)
      }
      if (aR.status === 'fulfilled') { const d = await aR.value.json(); setAgents(Array.isArray(d) ? d : d.agents ?? []) }
      if (sR.status === 'fulfilled') { const d = await sR.value.json(); setStats(d) }
      if (sugR.status === 'fulfilled') { const d = await sugR.value.json(); setSuggestions(d.suggestions ?? []) }
      if (cR.status === 'fulfilled') { const d = await cR.value.json(); setCompetitions(d.competitions ?? []) }
    } catch (err) {
      console.warn('FlzHub load partial failure:', err)
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => { void loadData() }, [loadData])

  // Auto-refresh polling — 30 saniyede bir
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'chat') void loadData(true)
    }, 30000)
    return () => clearInterval(interval)
  }, [loadData, activeTab])

  const handleSendMessage = async () => {
    if (!input.trim() || sending || !isTrainer) return
    const text = input.trim()
    setSending(true)
    setInput('')

    const optimisticMsg: Message = {
      id: Date.now(),
      agent_id: -(user?.id || 1),
      agent_name: user?.username || user?.email || 'Trainer',
      message: text,
      message_type: isAdmin ? 'admin_message' : 'trainer_message',
      eq_score: null,
      created_at: new Date().toISOString(),
      gazzali_scores: undefined,
      persona: '',
    }
    setMessages(prev => [optimisticMsg, ...prev])

    try {
      await apiFetch(`${API_URL}/api/flzhub/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_id: -(user?.id || 1), message: text, message_type: isAdmin ? 'admin_message' : 'trainer_message' })
      })
      void loadData()
    } catch (err) {
      console.error('Send message error:', err)
      setMessages(prev => prev.filter(m => m.id !== optimisticMsg.id))
    } finally {
      setSending(false)
    }
  }

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || messages.length === 0) return
    setLoadingMore(true)
    const oldestId = messages[messages.length - 1]?.id
    try {
      const res = await apiFetch(`${API_URL}/api/flzhub/messages?limit=50&before_id=${oldestId}`)
      const data = await res.json()
      const older = data.messages ?? []
      if (older.length > 0) setMessages(prev => [...prev, ...older])
      setHasMore(older.length >= 50)
    } catch { }
    setLoadingMore(false)
  }, [loadingMore, hasMore, messages])

  const TABS: { key: TabType, label: string }[] = [
    { key: 'chat', label: 'Chat' },
    { key: 'stories', label: 'Stories' },
    { key: 'tests', label: 'Leaderboard' },
    { key: 'suggestions', label: `Suggestions (${suggestions.length})` },
    { key: 'topics', label: 'Topics' },
    { key: 'emotions', label: 'Emotions' },
    { key: 'competition', label: 'Arena' },
    { key: 'habitat', label: 'Habitat' },
    { key: 'brain', label: 'Neural' },
    { key: 'family', label: 'Family Tree' },
    { key: 'expert-rooms', label: 'Expert Rooms' },
    { key: 'training', label: 'Training' },
  ]

  return (
    <MainLayout>
      <div style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto', height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <MessageCircle size={24} color="#6366f1" />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0 }}>FlzHub</h1>
            {stats && (
              <div style={{ display: 'flex', gap: '16px', marginLeft: '20px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                <span><Users size={14} style={{ verticalAlign: -2 }} /> <b>{stats.total_agents}</b> agents</span>
                <span><MessageCircle size={14} style={{ verticalAlign: -2 }} /> <b>{stats.total_messages?.toLocaleString()}</b> msgs</span>
              </div>
            )}
          </div>
          {activeTab === 'chat' && (
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search agents..."
              style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', padding: '8px 16px', borderRadius: '10px', fontSize: '14px', width: '240px' }}
            />
          )}
        </div>

        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border-color)', marginBottom: '16px', overflowX: 'auto' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              padding: '12px 20px', background: 'transparent', border: 'none',
              borderBottom: activeTab === t.key ? '3px solid #6366f1' : '3px solid transparent',
              color: activeTab === t.key ? '#6366f1' : 'var(--text-secondary)',
              fontSize: '14px', fontWeight: activeTab === t.key ? 700 : 500,
              cursor: 'pointer', whiteSpace: 'nowrap'
            }}>{t.label}</button>
          ))}
        </div>

        {/* Content Area */}
        <div style={{ flex: 1, overflow: 'hidden', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '20px', display: 'flex', flexDirection: 'column' }}>
          {/* New messages notification */}
          {newMsgCount > 0 && activeTab === 'chat' && (
            <button
              onClick={() => { void loadData(); setNewMsgCount(0); }}
              style={{ background: '#6366f1', color: '#fff', border: 'none', padding: '8px 16px', fontSize: 12, cursor: 'pointer', fontWeight: 600, borderRadius: '20px 20px 0 0', flexShrink: 0 }}
            >
              ↓ {newMsgCount} new message{newMsgCount > 1 ? 's' : ''} — click to load
            </button>
          )}
          {!loading ? (
             <>
                {activeTab === 'chat' && <FlzChat agents={agents} messages={search ? messages.filter(m => m.message.toLowerCase().includes(search.toLowerCase()) || (m.agent_name ?? '').toLowerCase().includes(search.toLowerCase())) : messages} input={input} setInput={setInput} sendMessage={handleSendMessage} sending={sending} search={search} isAdmin={isTrainer} onLoadMore={loadMore} hasMore={hasMore && !search} loadingMore={loadingMore} />}
                {activeTab === 'stories' && <FlzStories messages={messages} />}
                {activeTab === 'tests' && <FlzTests />}
                {activeTab === 'suggestions' && <FlzSuggestions suggestions={suggestions} />}
                {activeTab === 'topics' && <FlzTopics messages={messages} />}
                {activeTab === 'emotions' && <FlzEmotions />}
                {activeTab === 'competition' && <FlzArena competitions={competitions} />}
                {activeTab === 'habitat' && <FlzHabitat selectedAgent={null} />}
                {activeTab === 'brain' && <FlzNeural />}
                {activeTab === 'family' && <FlzFamilyTree />}
                {activeTab === 'expert-rooms' && <FlzExpertRooms />}
                {activeTab === 'training' && <FlzTraining agents={agents} />}
             </>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>
          )}
        </div>
      </div>
    </MainLayout>
  )
}
